import React from 'react';
import { Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { TEnfantAttribue } from '../../api/paiement/apporteurs';

// Liste des enfants attribués à un animateur pour un mois, avec leurs parents
// et leur école. Commune à l'espace de l'animateur et à l'administration.

const nomComplet = (prenom?: string, nom?: string) => [prenom, nom].filter(Boolean).join(' ');

const ListeEnfants: React.FC<{ enfants: TEnfantAttribue[]; loading?: boolean }> = ({ enfants, loading }) => {
  const { t } = useTranslation();

  const sources: Record<TEnfantAttribue['source'], string> = {
    inscription_parent: t('apporteur.liste.sourceInscription', 'Inscription du parent'),
    paiement: t('apporteur.liste.sourcePaiement', 'Code au paiement'),
    ecole: t('apporteur.liste.sourceEcole', 'École associée'),
    correction: t('apporteur.liste.sourceCorrection', 'Correction'),
  };

  const colonnes: ColumnsType<TEnfantAttribue> = [
    {
      title: t('apporteur.liste.parents', 'Parent(s)'),
      key: 'parents',
      render: (_, e) =>
        e.parents.length
          ? e.parents.map((p) => <div key={p.id}>{nomComplet(p.prenom, p.nom) || `#${p.id}`}</div>)
          : '—',
    },
    {
      title: t('apporteur.liste.enfant', 'Enfant'),
      key: 'enfant',
      render: (_, e) =>
        e.supprime ? (
          <Typography.Text type="secondary">{t('apporteur.liste.compteSupprime', 'Compte supprimé')}</Typography.Text>
        ) : (
          nomComplet(e.prenom, e.nom) || `#${e.enfant_id}`
        ),
    },
    {
      title: t('apporteur.liste.ecole', 'École'),
      key: 'ecole',
      render: (_, e) => (e.ecoles.length ? e.ecoles.map((x) => <div key={x.id}>{x.nom}</div>) : '—'),
    },
    {
      title: t('apporteur.liste.statut', 'Statut'),
      key: 'statut',
      render: (_, e) =>
        e.statut === 'premium' ? (
          <Tag color="gold">{t('apporteur.premium', 'Premium')}</Tag>
        ) : (
          <Tag>{t('apporteur.freemium', 'Freemium')}</Tag>
        ),
    },
    {
      title: t('apporteur.liste.depuis', 'Attribué depuis'),
      key: 'depuis',
      render: (_, e) => (
        <span>
          {dayjs(e.depuis).format('DD/MM/YYYY')}
          <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
            {sources[e.source]}
          </Typography.Text>
        </span>
      ),
    },
  ];

  return (
    <Table
      rowKey="enfant_id"
      columns={colonnes}
      dataSource={enfants}
      loading={loading}
      pagination={{ pageSize: 20, hideOnSinglePage: true }}
      scroll={{ x: true }}
      locale={{ emptyText: t('apporteur.liste.vide', 'Aucun enfant attribué ce mois-ci') }}
    />
  );
};

export default ListeEnfants;
